import { useEffect, useRef, useState } from "react";
import api from "../../api/client";

function Modal({ open, title, onClose, children }) {
  if (!open) return null;
  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-xl shadow-xl w-full max-w-md">
        <div className="flex items-center justify-between px-5 py-4 border-b">
          <h3 className="font-semibold text-gray-800">{title}</h3>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600 text-xl">×</button>
        </div>
        <div className="px-5 py-4">{children}</div>
      </div>
    </div>
  );
}

function DragHandle() {
  return (
    <svg width="16" height="16" viewBox="0 0 16 16" fill="currentColor" className="text-gray-300 group-hover:text-gray-400 transition-colors flex-shrink-0">
      <circle cx="5" cy="4" r="1.2"/><circle cx="11" cy="4" r="1.2"/>
      <circle cx="5" cy="8" r="1.2"/><circle cx="11" cy="8" r="1.2"/>
      <circle cx="5" cy="12" r="1.2"/><circle cx="11" cy="12" r="1.2"/>
    </svg>
  );
}

const EMPTY = { nombre: "", descripcion: "", activo: true };

export default function Categorias() {
  const [cats, setCats] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [guardando, setGuardando] = useState(false);
  const [modal, setModal] = useState(false);
  const [form, setForm] = useState(EMPTY);
  const [editId, setEditId] = useState(null);
  const [error, setError] = useState("");

  // Drag state
  const dragIdx = useRef(null);
  const [overIdx, setOverIdx] = useState(null);

  const cargar = () => {
    setLoading(true);
    api.get("/categorias/?page=1&page_size=100")
      .then((r) => {
        const items = [...r.data.items].sort((a, b) => a.orden - b.orden);
        setCats(items);
        setTotal(r.data.total);
      })
      .finally(() => setLoading(false));
  };

  useEffect(cargar, []);

  /* ── Drag & drop ──────────────────────────────────────────────────────── */
  const onDragStart = (e, idx) => {
    dragIdx.current = idx;
    e.dataTransfer.effectAllowed = "move";
    // ghost image transparente para no mostrar el clon por defecto
    const ghost = document.createElement("div");
    ghost.style.position = "absolute";
    ghost.style.top = "-9999px";
    document.body.appendChild(ghost);
    e.dataTransfer.setDragImage(ghost, 0, 0);
    setTimeout(() => document.body.removeChild(ghost), 0);
  };

  const onDragOver = (e, idx) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = "move";
    if (idx !== overIdx) setOverIdx(idx);
  };

  const onDrop = async (e, idx) => {
    e.preventDefault();
    const from = dragIdx.current;
    dragIdx.current = null;
    setOverIdx(null);
    if (from === null || from === idx) return;

    const prevMap = Object.fromEntries(cats.map((c) => [c.id, c.orden]));
    const arr = [...cats];
    const [moved] = arr.splice(from, 1);
    arr.splice(idx, 0, moved);
    const updated = arr.map((c, i) => ({ ...c, orden: i + 1 }));
    setCats(updated);

    const changed = updated.filter((c) => c.orden !== prevMap[c.id]);
    if (!changed.length) return;
    setGuardando(true);
    try {
      await Promise.all(changed.map((c) => api.put(`/categorias/${c.id}`, { orden: c.orden })));
    } catch {
      // silencioso — el orden visual ya está actualizado
    } finally {
      setGuardando(false);
    }
  };

  const onDragEnd = () => {
    dragIdx.current = null;
    setOverIdx(null);
  };

  /* ── CRUD ─────────────────────────────────────────────────────────────── */
  const openNew = () => { setForm(EMPTY); setEditId(null); setError(""); setModal(true); };
  const openEdit = (c) => {
    setForm({ nombre: c.nombre, descripcion: c.descripcion || "", activo: c.activo });
    setEditId(c.id);
    setError("");
    setModal(true);
  };

  const guardar = async (e) => {
    e.preventDefault();
    setError("");
    try {
      if (editId) {
        await api.put(`/categorias/${editId}`, form);
      } else {
        const maxOrden = cats.length ? Math.max(...cats.map((c) => c.orden)) : 0;
        await api.post("/categorias/", { ...form, orden: maxOrden + 1 });
      }
      setModal(false);
      cargar();
    } catch (err) {
      setError(err.response?.data?.detail || "Error al guardar");
    }
  };

  const eliminar = async (id) => {
    if (!window.confirm("¿Eliminar esta categoría?")) return;
    try {
      await api.delete(`/categorias/${id}`);
      cargar();
    } catch (err) {
      alert(err.response?.data?.detail || "No se puede eliminar");
    }
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <div>
          <h2 className="text-2xl font-bold text-gray-900">Categorías</h2>
          <p className="text-gray-500 text-sm mt-1">
            {total} categorías en total
            {guardando && <span className="ml-2 text-indigo-500 text-xs animate-pulse">· Guardando orden…</span>}
          </p>
        </div>
        <button
          onClick={openNew}
          className="bg-indigo-600 hover:bg-indigo-700 text-white text-sm font-medium px-4 py-2 rounded-lg transition-colors"
        >
          + Nueva categoría
        </button>
      </div>

      <div className="bg-white rounded-xl shadow-sm overflow-hidden">
        {loading ? (
          <div className="flex items-center justify-center h-40">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600"></div>
          </div>
        ) : cats.length === 0 ? (
          <div className="text-center py-16 text-gray-400">
            <div className="text-5xl mb-3">🏷️</div>
            <p className="font-medium">No hay categorías aún</p>
            <p className="text-sm mt-1">Crea tu primera categoría</p>
          </div>
        ) : (
          <>
            <div className="px-5 pt-3 pb-2 flex items-center justify-between gap-4 flex-wrap">
              <p className="text-xs text-gray-400 flex items-center gap-1.5">
                <span>⠿</span> Arrastra las filas para cambiar el orden en el catálogo
              </p>
              <p className="text-xs text-amber-600 bg-amber-50 border border-amber-200 rounded-lg px-3 py-1.5 leading-tight">
                ⚠️ Las categorías <strong>sin productos</strong> no aparecen en el catálogo público aunque tengan un número de orden asignado.
              </p>
            </div>
            <table className="w-full text-sm">
              <thead className="bg-gray-50 text-gray-500 uppercase text-xs">
                <tr>
                  <th className="px-3 py-3 w-10"></th>
                  <th className="px-2 py-3 text-center w-14">Orden</th>
                  <th className="px-4 py-3 text-left">Nombre</th>
                  <th className="px-4 py-3 text-left hidden md:table-cell">Descripción</th>
                  <th className="px-4 py-3 text-center">Productos</th>
                  <th className="px-4 py-3 text-center hidden sm:table-cell">En catálogo</th>
                  <th className="px-4 py-3 text-center">Estado</th>
                  <th className="px-4 py-3 text-center">Acciones</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {cats.map((c, idx) => {
                  const isDragging = dragIdx.current === idx;
                  const isOver = overIdx === idx && dragIdx.current !== null && dragIdx.current !== idx;
                  const visibleEnMenu = c.activo && c.total_productos > 0;
                  return (
                    <tr
                      key={c.id}
                      draggable
                      onDragStart={(e) => onDragStart(e, idx)}
                      onDragOver={(e) => onDragOver(e, idx)}
                      onDrop={(e) => onDrop(e, idx)}
                      onDragEnd={onDragEnd}
                      className={`group transition-colors ${isDragging ? "opacity-40 bg-indigo-50" : isOver ? "bg-indigo-50 border-t-2 border-indigo-400" : visibleEnMenu ? "hover:bg-gray-50" : "bg-gray-50/60 hover:bg-gray-100/60"}`}
                      style={{ cursor: "grab" }}
                    >
                      <td className="px-3 py-3">
                        <div className="flex items-center justify-center">
                          <DragHandle />
                        </div>
                      </td>
                      <td className="px-2 py-3 text-center">
                        <span className={`text-xs font-semibold ${visibleEnMenu ? "text-indigo-500" : "text-gray-300"}`}>{c.orden}</span>
                      </td>
                      <td className={`px-4 py-3 font-medium ${visibleEnMenu ? "text-gray-800" : "text-gray-400"}`}>{c.nombre}</td>
                      <td className="px-4 py-3 text-gray-400 hidden md:table-cell truncate max-w-xs">{c.descripcion || "—"}</td>
                      <td className="px-4 py-3 text-center text-gray-600">{c.total_productos}</td>
                      <td className="px-4 py-3 text-center hidden sm:table-cell">
                        {visibleEnMenu ? (
                          <span className="inline-flex items-center gap-1 text-xs font-medium text-green-600">
                            <span>✓</span> Visible
                          </span>
                        ) : (
                          <span className="inline-flex items-center gap-1 text-xs font-medium text-gray-400">
                            <span>—</span> Oculta
                          </span>
                        )}
                      </td>
                      <td className="px-4 py-3 text-center">
                        <span className={`inline-block px-2 py-0.5 rounded-full text-xs font-medium ${c.activo ? "bg-green-100 text-green-700" : "bg-gray-100 text-gray-500"}`}>
                          {c.activo ? "Activa" : "Inactiva"}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-center">
                        <div className="flex items-center justify-center gap-2">
                          <button onClick={() => openEdit(c)} className="text-indigo-600 hover:text-indigo-800 text-xs font-medium">Editar</button>
                          <button onClick={() => eliminar(c.id)} className="text-red-500 hover:text-red-700 text-xs font-medium">Eliminar</button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </>
        )}
      </div>

      <Modal open={modal} title={editId ? "Editar categoría" : "Nueva categoría"} onClose={() => setModal(false)}>
        {error && <div className="bg-red-50 border border-red-200 text-red-700 rounded-lg p-3 text-sm mb-3">{error}</div>}
        <form onSubmit={guardar} className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Nombre *</label>
            <input
              required
              value={form.nombre}
              onChange={(e) => setForm({ ...form, nombre: e.target.value })}
              className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
              placeholder="Ej: Bebidas"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Descripción</label>
            <textarea
              value={form.descripcion}
              onChange={(e) => setForm({ ...form, descripcion: e.target.value })}
              rows={2}
              className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 resize-none"
              placeholder="Descripción opcional"
            />
          </div>
          <label className="flex items-center gap-2 cursor-pointer">
            <input
              type="checkbox"
              checked={form.activo}
              onChange={(e) => setForm({ ...form, activo: e.target.checked })}
              className="rounded border-gray-300 text-indigo-600"
            />
            <span className="text-sm text-gray-700">Activa</span>
          </label>
          <div className="flex gap-3 pt-2">
            <button type="button" onClick={() => setModal(false)} className="flex-1 border border-gray-300 text-gray-700 py-2 rounded-lg text-sm hover:bg-gray-50">Cancelar</button>
            <button type="submit" className="flex-1 bg-indigo-600 text-white py-2 rounded-lg text-sm hover:bg-indigo-700">
              {editId ? "Guardar cambios" : "Crear categoría"}
            </button>
          </div>
        </form>
      </Modal>
    </div>
  );
}
